import { gql, useQuery } from '@apollo/client';
import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import EditButton from '@orn/shared-web/components/Button/EditButton';
import Table, { TableCard } from '@orn/shared-web/components/Table';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { formatCalendarDate, formatExpectedDaysUntil, toCurrency } from '../../../utils/formatting';
import {
  calculateYearlySpend,
  formatRecurringType,
  getAverageAmountLabel,
  getFrequencyLabel,
} from '../../../utils/recurring';
import Entity from '../../entity';
import RecurringTransactionSlideout from '../../slideouts/RecurringTransactionSlideout';
import UpdateRecurringTransactionModal from '../../UpdateRecurringTransactionModal';
import { AccountSecondaryElement, MerchantPrimaryElement } from '../elements';
import { ErrorTableState, LoadingTableState } from '../helpers';

import type {
  GetRecurringTransactionsForUserQuery,
  GetRecurringTransactionsForUserQueryVariables,
} from './__generated__/RecurringTransactionsTable.gql';

const GET_RECURRING_TRANSACTIONS_FOR_USER = gql`
  query GetRecurringTransactionsForUser($filters: RecurringTransactionFilters) {
    getRecurringTransactionsforUser(filters: $filters) {
      id
      description
      type
      frequency
      nextOccurrence
      amountRange
      rawJson
      merchant {
        id
        ...EntityAvatar
      }
      financialAccount {
        id
        name
        mask
        subtype
        ...EntityAvatar
        bank {
          id
          name
        }
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

export type RecurringTransaction = NonNullable<
  GetRecurringTransactionsForUserQuery['getRecurringTransactionsforUser']
>[number];

// `formatExpectedDaysUntil` can yield long labels ("Expected 3 weeks ago"); let
// those wrap instead of forcing the date column wider on a single line.
const DATE_LABEL_WRAP_THRESHOLD_CHARS = 12;

interface IBaseRecurringTransactionsTableProps {
  recurringTransactions: RecurringTransaction[];
  isLoading?: boolean;
  hasError?: boolean;
}

export const BaseRecurringTransactionsTable = ({
  recurringTransactions,
  isLoading = false,
  hasError = false,
}: IBaseRecurringTransactionsTableProps) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [selectedRecurringTransactionId, setSelectedRecurringTransactionId] = React.useState<string | null>(null);
  const [editingTransaction, setEditingTransaction] = React.useState<RecurringTransaction | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);

  const sortedTransactions = React.useMemo(() => {
    return [...recurringTransactions].sort((a, b) => {
      if (!a.nextOccurrence && !b.nextOccurrence) {
        return 0;
      }
      if (!a.nextOccurrence) {
        return 1;
      }
      if (!b.nextOccurrence) {
        return -1;
      }
      return a.nextOccurrence.compare(b.nextOccurrence);
    });
  }, [recurringTransactions]);

  const yearlyTotal = React.useMemo(() => {
    return sortedTransactions.reduce((total, txn) => {
      return total + calculateYearlySpend(txn.amountRange, txn.frequency);
    }, 0);
  }, [sortedTransactions]);

  const subtitle =
    !isLoading && sortedTransactions.length > 0 ? (
      <span className="flex items-center gap-1">
        <span className="text-primary text-xs font-medium">{sortedTransactions.length} Recurring</span>
        <span className="text-tertiary text-xs font-medium">({toCurrency(yearlyTotal)} per year)</span>
      </span>
    ) : undefined;

  const isMdOrUp = useBreakpoint('md');
  const isLgOrUp = useBreakpoint('lg');
  const tableSize = isLgOrUp ? 'md' : 'sm';

  return (
    <TableCard.Root className="min-w-0" size={tableSize}>
      <TableCard.Header
        title={'All Recurring'}
        subtitle={subtitle}
        trailingInline
        contentTrailing={<CollapseButton isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />}
      />
      {!isCollapsed && (
        <>
          {isLoading && <LoadingTableState />}
          {!isLoading && hasError && <ErrorTableState />}
          {!isLoading && !hasError && sortedTransactions.length === 0 && (
            <div className="flex h-56 items-center justify-center px-5 text-center">
              <div className="text-primary text-lg font-medium">No recurring transactions found</div>
            </div>
          )}
          {!isLoading && !hasError && sortedTransactions.length > 0 && (
            <Table aria-label="Recurring transactions table" className="w-full">
              <Table.Header>
                <Table.Head label="Date" className="pr-4" isRowHeader />
                <Table.Head label="Merchant" className="min-w-0 px-4" />
                {isMdOrUp && <Table.Head label="Frequency" className="px-4" />}
                {isLgOrUp && <Table.Head label="Account" className="px-4" />}
                <Table.Head label="Amount" className="flex justify-end px-4" />
              </Table.Header>
              <Table.Body dependencies={[isMdOrUp, isLgOrUp]} items={sortedTransactions}>
                {(recurringTransaction) => {
                  const expectedLabel = recurringTransaction.nextOccurrence
                    ? formatExpectedDaysUntil(recurringTransaction.nextOccurrence)
                    : null;

                  const merchant = recurringTransaction.merchant;
                  const type = formatRecurringType(recurringTransaction.type);
                  // Primary line is the description; fold the merchant name into the secondary
                  // line for identity, unless it would just duplicate the primary. The merchant
                  // name truncates while the type stays pinned and fully visible.
                  const secondaryText =
                    merchant && merchant.name !== recurringTransaction.description ? (
                      <span className="flex min-w-0">
                        <span className="min-w-0 truncate">{merchant.name}</span>
                        <span className="shrink-0">&nbsp;- {type}</span>
                      </span>
                    ) : (
                      type
                    );

                  return (
                    <Table.Row
                      key={recurringTransaction.id}
                      className="group/row h-18 cursor-pointer"
                      onClick={() => setSelectedRecurringTransactionId(recurringTransaction.id)}
                    >
                      {/* py-3 + leading-tight (below) keep a wrapped two-line date within the 72px row */}
                      <Table.Cell className="w-0 px-4 py-3">
                        {/* nextOccurrence re-check narrows its type for formatCalendarDate below */}
                        {expectedLabel !== null && recurringTransaction.nextOccurrence ? (
                          <Tooltip title={formatCalendarDate(recurringTransaction.nextOccurrence, 'medium')}>
                            <TooltipTrigger>
                              <div
                                className={cx(
                                  // text-left: the date cell is a row-header <th>, which defaults
                                  // to centered — explicit so wrapped labels stay left-aligned.
                                  'text-primary text-left text-base font-medium',
                                  // Bound the width so long labels actually wrap; an unbounded
                                  // cell in this auto-layout table just stays single-line.
                                  expectedLabel.length > DATE_LABEL_WRAP_THRESHOLD_CHARS
                                    ? 'max-w-28 whitespace-normal break-words leading-tight'
                                    : 'whitespace-nowrap'
                                )}
                              >
                                {expectedLabel}
                              </div>
                            </TooltipTrigger>
                          </Tooltip>
                        ) : (
                          <div className="text-primary whitespace-nowrap text-base font-medium">Unknown</div>
                        )}
                      </Table.Cell>
                      <Table.Cell className="w-full min-w-0 max-w-0 px-4 py-3.5">
                        <div className="flex min-w-0 items-center gap-1">
                          <MerchantPrimaryElement
                            merchant={merchant}
                            displayNameOverride={recurringTransaction.description}
                            secondaryText={secondaryText}
                          />
                          {isMdOrUp && (
                            <EditButton
                              aria-label="Edit name"
                              className="shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/row:opacity-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTransaction(recurringTransaction);
                                setIsEditModalOpen(true);
                              }}
                            />
                          )}
                        </div>
                      </Table.Cell>
                      {isMdOrUp && (
                        <Table.Cell className="w-28 p-4">
                          <div className="text-tertiary text-sm font-normal">
                            {getFrequencyLabel(recurringTransaction.frequency)}
                          </div>
                        </Table.Cell>
                      )}
                      {isLgOrUp && (
                        <Table.Cell className="w-28 p-4">
                          <AccountSecondaryElement financialAccount={recurringTransaction.financialAccount} />
                        </Table.Cell>
                      )}
                      <Table.Cell className="w-0 min-w-28 p-4">
                        <div className="text-primary text-right text-base font-medium">
                          {getAverageAmountLabel(recurringTransaction.amountRange)}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                }}
              </Table.Body>
            </Table>
          )}
        </>
      )}
      <RecurringTransactionSlideout
        recurringTransactionId={selectedRecurringTransactionId}
        isOpen={selectedRecurringTransactionId !== null}
        onClose={() => setSelectedRecurringTransactionId(null)}
      />
      {editingTransaction && (
        <UpdateRecurringTransactionModal
          recurringTransaction={editingTransaction}
          isOpen={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
        />
      )}
    </TableCard.Root>
  );
};

interface IRecurringTransactionsTableProps {
  financialAccountId?: string;
}

const RecurringTransactionsTable = ({ financialAccountId }: IRecurringTransactionsTableProps) => {
  const { data, loading, error } = useQuery<
    GetRecurringTransactionsForUserQuery,
    GetRecurringTransactionsForUserQueryVariables
  >(GET_RECURRING_TRANSACTIONS_FOR_USER, {
    variables: financialAccountId ? { filters: { financialAccountIds: [financialAccountId] } } : undefined,
  });

  const recurringTransactions = data?.getRecurringTransactionsforUser ?? [];

  return (
    <BaseRecurringTransactionsTable
      recurringTransactions={recurringTransactions}
      isLoading={loading}
      hasError={Boolean(error)}
    />
  );
};

export default RecurringTransactionsTable;
